import { FormikConfig, FormikHelpers, useFormikContext } from 'formik';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { T } from '../../../translation/src';
import useOpen from '../../../hooks/useOpen';
import { RendererContext } from '../../../contexts/RendererContext';
import { writeArrayBuffer } from 'geotiff';
import { ProjectionSystems } from '../../../utils/constants';
import { Project } from '../../../types/graphqlTypes';
import type { Vector2 } from 'three';
import { FormikCheckBox } from '../../../components/formik/FormCheckBox';
import { FormikSelect } from '../../../components/formik/FormSelect';
import { Option } from '../../../components/inputs/Select';
import { toast } from 'react-toastify';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { useHeatmapLegendOverlay } from '../../../hooks/modules/project/useHeatmapLegendOverlay';

enum ImageExportType {
  TIFF = 'tiff',
  JPG = 'jpg',
  PNG = 'png',
}

enum GeoTiffMode {
  ROTATED = 'rotated',
  TRANSFORM = 'transform',
}

const exportTypes = [
  { value: ImageExportType.TIFF, label: 'TIFF' },
  { value: ImageExportType.JPG, label: 'JPG' },
  { value: ImageExportType.PNG, label: 'PNG' },
];

const geoTiffModes = [
  { value: GeoTiffMode.ROTATED, labelKey: 'Rotated (max compatibility)' },
  { value: GeoTiffMode.TRANSFORM, labelKey: 'Original with ModelTransformation' },
];

interface FormValues {
  includeObjects: boolean;
  includeHeatmapLegend: boolean;
  fileType: ImageExportType;
  geoTiffMode: GeoTiffMode;
  resolutionMultiplier: number;
}

interface ScreenshotParameters {
  origin: Vector2;
  width: number;
  height: number;
  data?: { pixelWidth: number; pixelHeight: number };
}

interface OrthophotoModalProps {
  project: Pick<Project, 'id' | 'name' | 'description' | 'previewUrl' | 'settings'>;
}

function isProjectionSystem(value?: string | null): value is keyof typeof ProjectionSystems {
  if (!value) return false;
  return value in ProjectionSystems;
}

/**
 * Conditionally renders the GeoTIFF mode select only when TIFF is selected
 */
const GeoTiffModeSelect: React.FC = () => {
  const { values } = useFormikContext<FormValues>();

  if (values.fileType !== ImageExportType.TIFF) {
    return null;
  }

  return (
    <FormikSelect name="geoTiffMode" label={<T _str="GeoTIFF mode" />}>
      {geoTiffModes.map((option) => (
        <Option key={option.value} value={option.value}>
          <T _str={option.labelKey} />
        </Option>
      ))}
    </FormikSelect>
  );
};

/**
 * Resolution multiplier slider (1x to 2x)
 */
const ResolutionMultiplierSlider: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  return (
    <label className="block relative text-black dark:text-white mb-4">
      <span className="text-sm font-medium">
        <T _str="Resolution multiplier" />
      </span>
      <div className="flex items-center gap-3 mt-1">
        <input
          type="range"
          min={1}
          max={2}
          step={0.1}
          value={values.resolutionMultiplier}
          onChange={(e) => setFieldValue('resolutionMultiplier', parseFloat(e.target.value))}
          className="flex-1 accent-neon-green-700 dark:accent-neon-green-300 h-2"
        />
        <span className="w-12 text-sm font-medium text-right">{values.resolutionMultiplier.toFixed(1)}x</span>
      </div>
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
        <span>1x</span>
        <span>2x</span>
      </div>
    </label>
  );
};

const ExportOrthophotoModal_: React.FC2<OrthophotoModalProps> = ({ project }) => {
  const { onClose: closeOrthophotoModal, onOpenWithValue: openOrthophotoModalWithValue, value: screenshotParameters, open: orthophotoModalOpen } = useOpen<ScreenshotParameters>();
  const [loading, setLoading] = useState(false);
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const validationTranslations = useValidationTranslations();
  const { overlayVisible: isHeatmapLegendVisible } = useHeatmapLegendOverlay(project.id);

  // Skip legend on extremely narrow screenshots where the legend would not fit the image
  const width = screenshotParameters?.width || screenshotParameters?.data?.pixelWidth;
  const height = screenshotParameters?.height || screenshotParameters?.data?.pixelHeight;
  const ratio = width && height ? width / height : 0;
  const canDrawLegend = ratio >= 0.15 && ratio <= 3.5;

  const onOrthoScreenshot = useCallback(
    async ({ value }: { value?: ScreenshotParameters }) => {
      if (value) openOrthophotoModalWithValue(value);
    },
    [openOrthophotoModalWithValue],
  );

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_ortho_screenshot', onOrthoScreenshot);
    return () => {
      viewer.scene.removeEventListener('pointorama_ortho_screenshot', onOrthoScreenshot);
    };
  }, [onOrthoScreenshot, viewer]);

  const onSuccess = useCallback(
    ({ helpers: { resetForm } }: { helpers: FormikHelpers<FormValues> }) => {
      closeOrthophotoModal();
      // Because of transition
      setTimeout(() => {
        resetForm();
        setLoading(false);
      }, 250);
    },
    [closeOrthophotoModal],
  );

  const onCancel = useCallback(() => {
    closeOrthophotoModal();
  }, [closeOrthophotoModal]);

  const exportTiff = useCallback(
    async (includeObjects: boolean, includeHeatmapLegend: boolean, parameters: ScreenshotParameters, mode: GeoTiffMode, resolutionMultiplier: number) => {
      if (!viewer) return;
      const { origin, width, height } = parameters;

      // Use the appropriate screenshot method based on whether to include overlays
      let data;
      if (includeHeatmapLegend) {
        data = await viewer.screenshotRenderer.renderScreenshotWithOverlays(origin, width, height, includeObjects, resolutionMultiplier);
      } else {
        data = viewer.screenshotRenderer.renderScreenshot(origin, width, height, includeObjects, resolutionMultiplier);
      }
      const projectionSystem = project.settings?.projectionSystem;
      const isValidProjectionSystem = isProjectionSystem(projectionSystem);
      // Use EPSG:32767 for user-defined/local coordinate systems (no geodetic relationship)
      const epsgCode = isValidProjectionSystem ? ProjectionSystems[projectionSystem].epsgCode : 32767;

      // Get the pixel-to-world transformation vectors from the screenshot tool
      const { pixelToWorldX, pixelToWorldY } = data;

      // Use native coordinates directly - no transformation needed
      // This preserves maximum accuracy and avoids projection errors
      const topLeft = { x: data.position.x, y: data.position.y };
      const topRight = {
        x: data.position.x + pixelToWorldX.x * data.pixelWidth,
        y: data.position.y + pixelToWorldX.y * data.pixelWidth,
      };
      const bottomLeft = {
        x: data.position.x + pixelToWorldY.x * data.pixelHeight,
        y: data.position.y + pixelToWorldY.y * data.pixelHeight,
      };
      const bottomRight = {
        x: data.position.x + pixelToWorldX.x * data.pixelWidth + pixelToWorldY.x * data.pixelHeight,
        y: data.position.y + pixelToWorldX.y * data.pixelWidth + pixelToWorldY.y * data.pixelHeight,
      };

      let finalBuffer: Uint8Array;
      let geoTiffMetadata: Record<string, unknown>;

      if (mode === GeoTiffMode.TRANSFORM) {
        // Use ModelTransformation matrix - keeps original image, embeds rotation in metadata
        finalBuffer = new Uint8Array(data.buffer);

        // Calculate per-pixel vectors in native coordinates with high precision
        // To ensure a valid orthogonal transformation matrix, we use careful numerical methods
        // to minimize floating-point rounding errors.

        // Calculate delta vectors directly (avoid intermediate rounding)
        const deltaXx = topRight.x - topLeft.x;
        const deltaXy = topRight.y - topLeft.y;
        const deltaYx = bottomLeft.x - topLeft.x;
        const deltaYy = bottomLeft.y - topLeft.y;

        // Calculate per-pixel vectors using double-precision arithmetic
        const rawPixelToWorldX = {
          x: deltaXx / data.pixelWidth,
          y: deltaXy / data.pixelWidth,
        };
        const rawPixelToWorldY = {
          x: deltaYx / data.pixelHeight,
          y: deltaYy / data.pixelHeight,
        };

        // Use Math.hypot for more numerically stable magnitude calculation
        // hypot(a, b) = sqrt(a^2 + b^2) but avoids overflow/underflow
        const scaleX = Math.hypot(rawPixelToWorldX.x, rawPixelToWorldX.y);
        const scaleY = Math.hypot(rawPixelToWorldY.x, rawPixelToWorldY.y);

        // CRITICAL FIX: Use average scale to ensure both row AND column orthogonality
        // Some GeoTIFF readers check column orthogonality instead of row orthogonality
        // Using different scales for X and Y would make columns non-orthogonal
        const averageScale = (scaleX + scaleY) / 2;

        // Extract rotation angle from X-axis vector (use single angle for orthogonality)
        const rotationAngle = Math.atan2(rawPixelToWorldX.y, rawPixelToWorldX.x);

        // Calculate cos/sin with full double precision
        const cos = Math.cos(rotationAngle);
        const sin = Math.sin(rotationAngle);

        // Reconstruct perfectly orthogonal transformation vectors using consistent cos/sin
        // and UNIFORM scale to ensure orthogonality in both row and column interpretation
        // This guarantees: rows orthogonal AND columns orthogonal (within machine precision)
        // X-axis: (scale * cos, scale * sin)
        // Y-axis: perpendicular to X, pointing "down" in image space = (scale * sin, -scale * cos)

        // First, compute the normalized X-axis direction
        const xDirX = cos;
        const xDirY = sin;

        // Compute perpendicular Y-axis direction (rotate X by -90 degrees)
        // For a 2D rotation by -90°: (x, y) -> (y, -x)
        const yDirX = sin;
        const yDirY = -cos;

        // Apply UNIFORM scale factor to get final transformation vectors
        // This ensures both rows and columns are orthogonal
        const pixelToWorldXTransform = {
          x: averageScale * xDirX,
          y: averageScale * xDirY,
        };
        const pixelToWorldYTransform = {
          x: averageScale * yDirX,
          y: averageScale * yDirY,
        };

        // Build a 4x4 affine transformation matrix for GeoTIFF in row-major order
        // Maps pixel (col, row) to world (X, Y)
        // X = d + (a * col) + (b * row)
        // Y = h + (e * col) + (f * row)
        // Matrix in row-major order:
        // [a, b, c, d,     <- Row 1: X transformation
        //  e, f, g, h,     <- Row 2: Y transformation
        //  i, j, k, l,     <- Row 3: Z transformation (unused for 2D)
        //  m, n, o, p]     <- Row 4: Homogeneous coordinate
        const modelTransformation = [
          pixelToWorldXTransform.x, // a: X scale in X direction
          pixelToWorldXTransform.y, // b: X scale in Y direction
          0, // c: (unused in 2D)
          topLeft.x, // d: X translation
          pixelToWorldYTransform.x, // e: Y scale in X direction
          pixelToWorldYTransform.y, // f: Y scale in Y direction
          0, // g: (unused in 2D)
          topLeft.y, // h: Y translation
          0, // i: (unused in 2D)
          0, // j: (unused in 2D)
          1, // k: Z scale
          0, // l: Z translation
          0, // m
          0, // n
          0, // o
          1, // p
        ];

        geoTiffMetadata = {
          ImageWidth: data.pixelWidth,
          ImageLength: data.pixelHeight,
          BitsPerSample: [8, 8, 8, 8],
          SamplesPerPixel: 4,
          PhotometricInterpretation: 2, // RGB(A)
          PlanarConfiguration: 1,
          ModelTransformation: modelTransformation,
          GTModelTypeGeoKey: 1, // ModelTypeProjected
          GTRasterTypeGeoKey: 1, // RasterPixelIsArea
          ProjectedCSTypeGeoKey: epsgCode, // Use native projection EPSG code (32767 for local coordinates)
          ProjLinearUnitsGeoKey: 9001, // Linear_Meter
        };
      } else {
        // Rotated mode - rotate image to be axis-aligned, use ModelTiepoint + ModelPixelScale
        // Calculate the axis-aligned bounding box in native coordinates
        const minX = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const maxX = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const minY = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        const maxY = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);

        // Calculate the rotation angle from the pixel vectors
        const rotationAngle = Math.atan2(pixelToWorldX.y, pixelToWorldX.x);

        // Create a canvas to rotate the image
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = data.pixelWidth;
        sourceCanvas.height = data.pixelHeight;
        const sourceCtx = sourceCanvas.getContext('2d');
        if (!sourceCtx) return;

        // Put the original image data onto the source canvas
        const imageData = new ImageData(new Uint8ClampedArray(data.buffer), data.pixelWidth, data.pixelHeight);
        sourceCtx.putImageData(imageData, 0, 0);

        // Calculate the bounding box dimensions after rotation
        const absCos = Math.abs(Math.cos(rotationAngle));
        const absSin = Math.abs(Math.sin(rotationAngle));
        const rotatedBBWidth = Math.ceil(data.pixelWidth * absCos + data.pixelHeight * absSin);
        const rotatedBBHeight = Math.ceil(data.pixelWidth * absSin + data.pixelHeight * absCos);

        // Create a canvas for the rotated image (full bounding box)
        const rotatedCanvas = document.createElement('canvas');
        rotatedCanvas.width = rotatedBBWidth;
        rotatedCanvas.height = rotatedBBHeight;
        const rotatedCtx = rotatedCanvas.getContext('2d');
        if (!rotatedCtx) return;

        // Clear and rotate
        rotatedCtx.clearRect(0, 0, rotatedBBWidth, rotatedBBHeight);
        rotatedCtx.save();
        rotatedCtx.translate(rotatedBBWidth / 2, rotatedBBHeight / 2);
        rotatedCtx.rotate(-rotationAngle);
        rotatedCtx.drawImage(sourceCanvas, -data.pixelWidth / 2, -data.pixelHeight / 2);
        rotatedCtx.restore();

        // Find the bounding box of non-transparent pixels to crop
        const rotatedImageData = rotatedCtx.getImageData(0, 0, rotatedBBWidth, rotatedBBHeight);
        const rotatedData = rotatedImageData.data;

        let cropMinX = rotatedBBWidth;
        let cropMinY = rotatedBBHeight;
        let cropMaxX = 0;
        let cropMaxY = 0;

        for (let y = 0; y < rotatedBBHeight; y++) {
          for (let x = 0; x < rotatedBBWidth; x++) {
            const alpha = rotatedData[(y * rotatedBBWidth + x) * 4 + 3];
            if (alpha > 0) {
              if (x < cropMinX) cropMinX = x;
              if (x > cropMaxX) cropMaxX = x;
              if (y < cropMinY) cropMinY = y;
              if (y > cropMaxY) cropMaxY = y;
            }
          }
        }

        // Calculate cropped dimensions
        const croppedWidth = cropMaxX - cropMinX + 1;
        const croppedHeight = cropMaxY - cropMinY + 1;

        // Create final cropped canvas
        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = croppedWidth;
        croppedCanvas.height = croppedHeight;
        const croppedCtx = croppedCanvas.getContext('2d');
        if (!croppedCtx) return;

        croppedCtx.drawImage(rotatedCanvas, cropMinX, cropMinY, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight);

        const croppedImageData = croppedCtx.getImageData(0, 0, croppedWidth, croppedHeight);
        finalBuffer = new Uint8Array(croppedImageData.data);

        // Calculate the world coordinates for the cropped image
        const worldPerPixelX = (maxX - minX) / rotatedBBWidth;
        const worldPerPixelY = (maxY - minY) / rotatedBBHeight;

        const croppedMinX = minX + cropMinX * worldPerPixelX;
        const croppedMaxY = maxY - cropMinY * worldPerPixelY;

        // Calculate pixel scale for the cropped image
        const pixelScaleX = worldPerPixelX;
        const pixelScaleY = worldPerPixelY;

        geoTiffMetadata = {
          ImageWidth: croppedWidth,
          ImageLength: croppedHeight,
          BitsPerSample: [8, 8, 8, 8],
          SamplesPerPixel: 4,
          PhotometricInterpretation: 2, // RGB(A)
          PlanarConfiguration: 1,
          ModelTiepoint: [0, 0, 0, croppedMinX, croppedMaxY, 0],
          ModelPixelScale: [pixelScaleX, pixelScaleY, 0],
          GTModelTypeGeoKey: 1, // ModelTypeProjected
          GTRasterTypeGeoKey: 1, // RasterPixelIsArea
          ProjectedCSTypeGeoKey: epsgCode, // Use native projection EPSG code (32767 for local coordinates)
          ProjLinearUnitsGeoKey: 9001, // Linear_Meter
        };
      }

      const tiffData = await writeArrayBuffer(finalBuffer, geoTiffMetadata);

      const blob = new Blob([tiffData], { type: 'image/tiff' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = project.name || 'screenshot';
      link.click();
    },
    [viewer, project.name, project.settings?.projectionSystem],
  );

  const getImage = useCallback(
    async (includeObjects: boolean, includeHeatmapLegend: boolean, parameters: ScreenshotParameters, resolutionMultiplier: number) => {
      if (!viewer) return;
      const { origin, width, height } = parameters;

      // Use the appropriate screenshot method based on whether to include overlays
      let data;
      if (includeHeatmapLegend) {
        data = await viewer.screenshotRenderer.renderScreenshotWithOverlays(origin, width, height, includeObjects, resolutionMultiplier);
      } else {
        data = viewer.screenshotRenderer.renderScreenshot(origin, width, height, includeObjects, resolutionMultiplier);
      }

      const canvas = document.createElement('canvas');
      canvas.width = data.pixelWidth;
      canvas.height = data.pixelHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const imageData = new ImageData(new Uint8ClampedArray(data.buffer), data.pixelWidth, data.pixelHeight);

      ctx.putImageData(imageData, 0, 0);

      return canvas;
    },
    [viewer],
  );

  const exportPng = useCallback(
    async (includeObjects: boolean, includeHeatmapLegend: boolean, parameters: ScreenshotParameters, resolutionMultiplier: number) => {
      const canvas = await getImage(includeObjects, includeHeatmapLegend, parameters, resolutionMultiplier);

      canvas?.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = project.name || 'screenshot';
        link.click();
        URL.revokeObjectURL(url);
      }, 'image/png');
    },
    [project.name, getImage],
  );

  const exportJpg = useCallback(
    async (includeObjects: boolean, includeHeatmapLegend: boolean, parameters: ScreenshotParameters, resolutionMultiplier: number) => {
      const canvas = await getImage(includeObjects, includeHeatmapLegend, parameters, resolutionMultiplier);

      canvas?.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${project.name}.jpg`;
        link.click();
        URL.revokeObjectURL(url);
      }, 'image/jpg');
    },
    [getImage, project.name],
  );

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (options, helpers) => {
      if (!viewer || !screenshotParameters) return;
      setLoading(true);

      // Small delay to ensure the loading spinner is rendered before heavy rendering
      await new Promise((resolve) => setTimeout(resolve, 50));

      try {
        if (options.fileType === ImageExportType.TIFF) {
          await exportTiff(options.includeObjects, options.includeHeatmapLegend, screenshotParameters, options.geoTiffMode, options.resolutionMultiplier);
        } else if (options.fileType === ImageExportType.PNG) {
          await exportPng(options.includeObjects, options.includeHeatmapLegend, screenshotParameters, options.resolutionMultiplier);
        } else if (options.fileType === ImageExportType.JPG) {
          await exportJpg(options.includeObjects, options.includeHeatmapLegend, screenshotParameters, options.resolutionMultiplier);
        }
        onSuccess({ helpers });
      } catch {
        toast.error(validationTranslations.somethingWentWrong);
      } finally {
        setLoading(false);
      }
    },
    [viewer, onSuccess, exportTiff, screenshotParameters, exportPng, exportJpg, validationTranslations.somethingWentWrong],
  );

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: {
        includeObjects: false,
        includeHeatmapLegend: isHeatmapLegendVisible,
        fileType: ImageExportType.TIFF,
        geoTiffMode: GeoTiffMode.ROTATED,
        resolutionMultiplier: 1,
      },
      onSubmit,
    }),
    [onSubmit, isHeatmapLegendVisible],
  );

  return (
    <CreateModal
      title={<T _str="Export orthophoto" swc />}
      createButtonTitle={<T _str="export" swc />}
      formik={formik}
      onCancel={onCancel}
      open={orthophotoModalOpen}
      isSubmitting={loading}
      alwaysCancellable
      footerMessage={
        loading && (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            <T _str="Keep this window open while processing..." />
          </p>
        )
      }
    >
      <FormikSelect name="fileType" label={<T _str="Save as" />}>
        {exportTypes.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </FormikSelect>
      <GeoTiffModeSelect />
      <ResolutionMultiplierSlider />
      <FormikCheckBox name="includeObjects" label={<T _str="Include objects" />} />
      {isHeatmapLegendVisible && canDrawLegend && <FormikCheckBox name="includeHeatmapLegend" label={<T _str="Include heatmap legend" />} defaultChecked={true} />}
    </CreateModal>
  );
};

export const ExportOrthophotoModal = memo(ExportOrthophotoModal_);
